import clsx from 'clsx';

export interface StatCardProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  highlight?: boolean;
}

export function StatCard({
  label,
  value,
  sublabel,
  icon,
  trend,
  trendValue,
  highlight = false,
}: StatCardProps) {
  return (
    <div
      className={clsx(
        'rounded-xl border bg-white p-5 transition-shadow hover:shadow-md',
        highlight && 'ring-2 ring-primary-500'
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {sublabel && <p className="mt-1 text-sm text-gray-500">{sublabel}</p>}
        </div>
        {icon && (
          <div className="flex-shrink-0 p-2 bg-primary-50 rounded-lg text-primary-600">
            {icon}
          </div>
        )}
      </div>

      {trend && trendValue && (
        <div className="mt-3 flex items-center gap-1">
          {trend === 'up' && <TrendUpIcon className="w-4 h-4 text-green-500" />}
          {trend === 'down' && <TrendDownIcon className="w-4 h-4 text-red-500" />}
          {trend === 'neutral' && <TrendNeutralIcon className="w-4 h-4 text-gray-400" />}
          <span
            className={clsx(
              'text-sm font-medium',
              trend === 'up' && 'text-green-600',
              trend === 'down' && 'text-red-600',
              trend === 'neutral' && 'text-gray-500'
            )}
          >
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
}

export interface ProgressStatsRowProps {
  sessionsCount: number;
  avgClarity: number | null;
  /** null = never measured: shown as such, never as a percentage or a trend. */
  avgConfidence: number | null;
  bestStreak: number;
  currentStreak?: number;
}

export function ProgressStatsRow({
  sessionsCount,
  avgClarity,
  avgConfidence,
  bestStreak,
  currentStreak = 0,
}: ProgressStatsRowProps) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        label="Sessions Completed"
        value={sessionsCount}
        icon={<SessionsIcon className="w-5 h-5" />}
        trend="up"
        trendValue="+3 this week"
      />
      <StatCard
        label="Average Clarity"
        value={avgClarity === null ? 'Not measured' : `${avgClarity}%`}
        icon={<ClarityIcon className="w-5 h-5" />}
        trend={avgClarity !== null && avgClarity >= 75 ? 'up' : 'neutral'}
        trendValue={avgClarity === null ? '' : avgClarity >= 75 ? 'Above target' : 'Keep practicing'}
      />
      <StatCard
        label="Average Confidence"
        value={avgConfidence === null ? 'Not measured' : `${avgConfidence}%`}
        icon={<ConfidenceIcon className="w-5 h-5" />}
        trend={avgConfidence !== null && avgConfidence >= 70 ? 'up' : 'neutral'}
        trendValue={avgConfidence === null ? '' : avgConfidence >= 70 ? 'Strong' : 'Improving'}
      />
      <StatCard
        label="Best Streak"
        value={`${bestStreak} days`}
        sublabel={currentStreak > 0 ? `Current: ${currentStreak} days` : undefined}
        icon={<StreakIcon className="w-5 h-5" />}
        highlight={currentStreak >= bestStreak && currentStreak > 0}
      />
    </div>
  );
}

// Icon components
function SessionsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function ClarityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function ConfidenceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

function StreakIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function TrendUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  );
}

function TrendDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  );
}

function TrendNeutralIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
    </svg>
  );
}
